import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import Drawer from "@mui/material/Drawer";
import RestoreIcon from "@mui/icons-material/Restore";
import CloseIcon from "@mui/icons-material/Close";
import SideBarElement from "./SideBarElement";
import { useDispatch } from "react-redux";
import {
	setHistoryDrawerState,
	showError,
} from "../../../redux/common/actions";
import { CircularProgress } from "@mui/material";
import useInfiniteScrollWithoutCount from "hooks/useInfiniteScrollWithoutCount";

import { coalesc, defaultPageSize } from "helpers/utils";
import TaskSubTable from "./TaskSubTable";

// ClientUserSiteAppServiceModels is an exclusion list: a row present means the
// model is blocked, so an Add/Delete on that table is the opposite of what the
// user did (granting access deletes the exclusion row, and vice versa). The
// delete itself is a soft delete (an "IsDeleted" property edit), so it must be
// normalized to its real Deleted/Restored type before this inversion runs.
const CLIENT_USER_SITE_APP_SERVICE_MODEL_TABLE = 95;
const ADDED_AUDIT_TYPE = 1;
const DELETED_AUDIT_TYPE = 3;
const RESTORED_AUDIT_TYPE = 5;

const invertModelExclusionAuditType = (auditType) => {
	// The row existing (freshly added, or un-soft-deleted) is what puts the
	// exclusion into effect, so both read as the access being removed; only
	// the soft delete lifts the exclusion, reading as the access being added.
	if (auditType === ADDED_AUDIT_TYPE || auditType === RESTORED_AUDIT_TYPE)
		return DELETED_AUDIT_TYPE;
	if (auditType === DELETED_AUDIT_TYPE) return ADDED_AUDIT_TYPE;
	return auditType;
};

const getDisplayEntry = (entry) => {
	// A soft delete arrives as an "IsDeleted" property edit rather than a real
	// delete/restore audit row, so remap it to the type that matches its value.
	const normalizedAuditType =
		entry?.auditType === 2 && entry?.propertyName === "IsDeleted"
			? entry?.newValue === "False"
				? RESTORED_AUDIT_TYPE
				: DELETED_AUDIT_TYPE
			: entry?.auditType;

	if (entry?.table !== CLIENT_USER_SITE_APP_SERVICE_MODEL_TABLE)
		return { ...entry, auditType: normalizedAuditType };

	// Every row on this table is really just "is the model excluded or not" -
	// the changed property (IsDeleted, ID, ...) and its raw value ("True",
	// a version name, ...) are internal plumbing, not something to show the
	// user, so point the sidebar at the model's name instead (matching the
	// "name modelName" format the Models tile itself uses). Both name and
	// newValue are set: SideBarElement reads name directly for Deleted/
	// Restored rows and falls back to newValue only otherwise.
	const modelLabel = `${entry?.name} ${coalesc(entry?.modelName)}`;
	return {
		...entry,
		auditType: invertModelExclusionAuditType(normalizedAuditType),
		name: modelLabel,
		newValue: modelLabel,
	};
};

const useStyles = makeStyles()((theme) => ({
	container: {
		display: "flex",
		flexDirection: "column",
	},
	paper: {
		minWidth: 400,
		maxWidth: 500,
		overflowY: "hidden",
	},
	title: {
		padding: "10px",
		color: "#FFFFFF",
		backgroundColor: "#186CE9",
		display: "flex",
		justifyContent: "space-between",
	},
	titleWrapper: {
		display: "flex",
		alignItems: "center",
	},
	titleText: {
		fontSize: "22px",
		fontWeight: "500",
		marginLeft: "10px",
	},
	restore: {
		border: "2px solid",
		borderRadius: "100%",
		height: "28px",
		width: "28px",
		color: "#FFFFFF",
	},
	restoreBtn: {
		border: "none",
		backgroundColor: "#186CE9",
	},
	closeBtn: {
		border: "none",
		color: "#FFFFFF",
		backgroundColor: "transparent",
	},
	closeIcon: {
		"&:hover": {
			cursor: "pointer",
		},
	},
	loadingIcon: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		marginTop: "10px",
	},
	historyBody: {
		overflowY: "auto",
		height: "100vh",
		paddingBottom: "50px",
	},
	historyBorder: {
		border: "1.5px solid",
		borderColor: "rgba(214, 212, 210,0.5)",
		width: "90%",
	},
	root: {},
}));

export default function HistoryBar({
	id,
	showhistorybar,
	dispatch,
	fetchdata,
	OnAddItemClick,
	hasSubTable = false,
	isQuestion = false,
	origin,
	statuses,
}) {
	const { t } = useTranslation();
	const { classes, cx } = useStyles();
	const reduxDispatch = useDispatch();
	const [history, SetHistory] = useState([]);
	const [IsLoading, setLoading] = useState(false);
	const [scrollEvent, setScrollEvent] = useState(window);
	const [page, setPage] = useState(1);
	const [hasMore, sethasMore] = useState(true);

	const histroyBodyRef = useRef(null);

	const { loading, gotoTop, hasMoreRef } = useInfiniteScrollWithoutCount(
		async (pageSize) => await onPageChange(pageSize + 1),
		page,
		scrollEvent,
		"history-body-scroll",
		{},
		// Only page while the drawer is open: closed, there is no history
		// container to fill, and hosts whose route has no history API pass a
		// fetchdata that must never be called (e.g. site app workbookfieldorder).
		showhistorybar
	);

	const onPageChange = async (p) => {
		try {
			const response = await fetchdata(id, p, defaultPageSize());
			if (response.status) {
				setPage(p);
				const finalData = response.data.map((d) => {
					return {
						...d,
						newValues: d?.newValues ? JSON.parse(d.newValues) : null,
						oldValues: d?.oldValues ? JSON.parse(d.oldValues) : null,
					};
				});
				SetHistory((prev) => [...prev, ...finalData]);
				if (response.data.length === 0) {
					sethasMore(false);
				}
				return response.data;
			} else {
				throw new Error(response);
			}
		} catch (err) {
			reduxDispatch(showError(t("message.couldNotFetchData")));
			return err;
		}
	};

	useEffect(() => {
		if (fetchdata && showhistorybar) {
			setLoading(true);
			fetchdata(id, 1, defaultPageSize())
				.then((response) => {
					if (response.status) {
						hasMoreRef.current = true;
						sethasMore(() => true);
						SetHistory(response.data);
						setLoading(false);
						setPage(() => 1);
					}
				})
				.catch((err) => {
					reduxDispatch(showError(t("message.couldNotFetchData")));
				});
		}
	}, [showhistorybar, reduxDispatch, id, fetchdata, hasMoreRef]);

	useEffect(
		() => {
			if (showhistorybar && histroyBodyRef.current) {
				// histroyBodyRef.current.addEventListener("scroll", (e) =>
				// 	handleScroll(e, "history-body-scroll", hasMore)
				// );
				setScrollEvent(histroyBodyRef.current);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[showhistorybar, histroyBodyRef.current]
	);

	return (
		<Drawer
			anchor="right"
			open={showhistorybar}
			SlideProps={{ unmountOnExit: true }}
			onClose={() => {
				// setPage(() => 1);
				dispatch({ type: "TOGGLE_HISTORYBAR" });
				reduxDispatch(setHistoryDrawerState(false));
			}}
			classes={{
				paper: classes.paper,
				root: classes.root,
			}}
		>
			<div className={classes.container}>
				<div className={classes.title}>
					<div className={classes.titleWrapper}>
						<button
							className={classes.restoreBtn}
							onClick={() => dispatch({ type: "TOGGLE_HISTORYBAR" })}
						>
							<RestoreIcon className={classes.restore} />
						</button>
						<span className={classes.titleText}>{t("title.history")}</span>
					</div>

					<button
						onClick={() => {
							dispatch({ type: "TOGGLE_HISTORYBAR" });
							reduxDispatch(setHistoryDrawerState(false));
						}}
						className={classes.closeBtn}
					>
						<CloseIcon className={classes.closeIcon} fontSize="large" />
					</button>
				</div>

				<div
					className={classes.historyBody}
					id="history-body-scroll"
					ref={histroyBodyRef}
				>
					{IsLoading ? (
						<div className={classes.loadingIcon}>
							<CircularProgress />
						</div>
					) : (
						<>
							{history
								?.filter(
									(data) =>
										!isQuestion ||
										data?.propertyName !== "DecimalPlaces" ||
										(data?.propertyName === "DecimalPlaces" && data?.newValue)
								)
								.map((d) => getDisplayEntry(d))
								.map((data) => (
									<>
										{hasSubTable && data.table !== 70 ? (
											<TaskSubTable
												IsLoading={IsLoading}
												setLoading={setLoading}
												key={data?.dateTime}
												data={data}
												OnAddItemClick={OnAddItemClick}
												origin={origin}
											/>
										) : (
											<SideBarElement
												IsLoading={IsLoading}
												setLoading={setLoading}
												key={data?.dateTime}
												data={data}
												OnAddItemClick={OnAddItemClick}
												origin={origin}
												statuses={statuses}
											/>
										)}
										<hr className={classes.historyBorder} />
									</>
								))}
							{loading && (
								<div className={classes.loadingIcon}>
									<CircularProgress />
								</div>
							)}
						</>
					)}

					{!hasMore && (
						<div
							style={{ textAlign: "center", padding: "16px 10px" }}
							className="flex justify-center"
						>
							<span
								className="link-color ml-md cursor-pointer"
								onClick={() => gotoTop()}
							>
								{t("action.goToTop")}
							</span>
						</div>
					)}
				</div>
			</div>
		</Drawer>
	);
}
